import React, { ChangeEvent } from 'react';

import PropTypes from 'prop-types';

import {
  StylableComponent,
  stylePropType,
  useControlled,
} from '@adsk/alloy-react-helpers';

import RadioGroupContext from './RadioGroupContext';

const RADIO_GROUP_VARIANTS = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
} as const;

type RadioGroupVariants = typeof RADIO_GROUP_VARIANTS;
type RadioGroupVariant = RadioGroupVariants[keyof RadioGroupVariants];

type RadioGroupProps = Omit<
  StylableComponent<HTMLDivElement>,
  'onChange' | 'defaultValue'
> & {
  name?: string;
  disabled?: boolean;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string | undefined, e: ChangeEvent) => void;
  variant?: RadioGroupVariant;
};
/**
 * A helpful wrapper for using multiple radio buttons.
 */
const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      style,
      className,
      name,
      value,
      onChange,
      defaultValue,
      variant = RADIO_GROUP_VARIANTS.VERTICAL,
      children,
      ...props
    },
    ref
  ) => {
    const [currentValue, handleOnChange] = useControlled(
      defaultValue,
      value,
      onChange
    );

    const context = {
      name,
      value: currentValue,
      onChange: handleOnChange,
    };

    return (
      <div
        role="radiogroup"
        {...props}
        className={className}
        ref={ref}
        css={[
          {
            display: 'flex',
            flexDirection:
              variant === RADIO_GROUP_VARIANTS.VERTICAL ? 'column' : 'row',
          },
          style,
        ]}
      >
        <RadioGroupContext.Provider value={context}>
          {children}
        </RadioGroupContext.Provider>
      </div>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

RadioGroup.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The name to propogate to the radio buttons */
  name: PropTypes.string,
  /** The value of the radio group (propagated as well to the radio buttons) */
  value: PropTypes.any,
  /** The initial value of the radio, this will be read only if `checked` is undefined */
  defaultValue: PropTypes.any,
  /** onChange handler for the radio input */
  onChange: PropTypes.func,
  /** The content of the radio group, should be `[RadioButton]` */
  children: PropTypes.any,
  /** style variant one of RadioGroup.DIRECTIONS */
  variant: PropTypes.oneOf(Object.values(RADIO_GROUP_VARIANTS)),
};

export default Object.assign(RadioGroup, { VARIANTS: RADIO_GROUP_VARIANTS });
